import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Medal, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";

const API = process.env.REACT_APP_BACKEND_URL;

/** Hall of Fame — Top 100 pilots by all-time best score. */
export default function Ranking() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/leaderboard/global?limit=100`);
      if (r.ok) setRows((await r.json()).leaderboard || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#050a18_0%,_#020408_60%,_#000_100%)] text-white pb-24">
      <div className="sticky top-0 z-20 border-b border-primary/20 bg-black/70 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => nav("/")}
            className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
            <ArrowLeft className="h-3 w-3" /> {t("ranking.back")}
          </button>
          <div className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground">{t("ranking.label")}</div>
          <div className="flex items-center gap-2">
            <button onClick={load}
              className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> {t("wallet.refresh")}
            </button>
            <LanguageSelector />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-10 space-y-8">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-amber-200">// TOP 100 · ALL-TIME</p>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">{t("ranking.title")}</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mt-2">{t("ranking.subtitle")}</p>
        </div>

        {loading ? (
          <p className="text-center py-12 text-muted-foreground font-mono text-xs tracking-widest">{t("rooms.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-12 text-muted-foreground font-mono text-xs tracking-widest border border-dashed border-primary/20 rounded-sm">
            {t("ranking.empty")}
          </p>
        ) : (
          <div className="space-y-2" data-testid="ranking-list">
            {rows.map((p, i) => {
              const pos = i + 1;
              const styleTop = pos === 1 ? "border-amber-300/70 bg-amber-500/10"
                          : pos === 2 ? "border-slate-300/60 bg-slate-500/10"
                          : pos === 3 ? "border-orange-400/60 bg-orange-500/10"
                          : "border-primary/20 bg-black/40";
              return (
                <div key={p.user_id} data-testid={`ranking-row-${pos}`}
                  className={`rounded-sm border ${styleTop} p-3 grid grid-cols-[auto_1fr_auto] gap-3 items-center hover:border-primary/60 transition-colors`}>
                  <div className={`h-10 w-10 rounded-sm border grid place-items-center font-display font-black text-lg ${pos <= 3 ? "border-amber-300 text-amber-200" : "border-primary/30 text-primary"}`}>
                    {pos}
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-black text-lg tracking-tighter truncate">
                      {pos <= 3 && <Medal className="inline h-4 w-4 mr-1 text-amber-200" />}
                      {p.display_name?.toUpperCase() || "PILOT"}
                    </p>
                    <p className="font-mono text-[10px] text-muted-foreground">
                      {t(p.runs === 1 ? "alien_id.trophy_max_level" : "alien_id.trophy_max_level_plural", { level: p.best_level, runs: p.runs })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-black text-xl text-primary">{(p.best_score || 0).toLocaleString()}</p>
                    <p className="font-mono text-[9px] text-muted-foreground tracking-widest">{t("ranking.columns.score")}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
